import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import type { ActiveUser, TechnicalActiveUser } from "@/api/active-users";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import { usageProfileLabel } from "@/components/projects/diagnostic/acts/environment/usageProfileLabel";
import {
  USAGE_PROFILE_ORDER,
  type UsageProfileBucket,
} from "@/components/projects/diagnostic/diagnosticData";
import { FilterButton } from "@/components/projects/diagnostic/orgStructure/FilterButton";
import { useActiveUsers } from "@/hooks/useActiveUsers";
import { formatCount } from "@/lib/format";

/** O card branco das telas de detalhe, o mesmo do WRICEF e dos gaps. */
const CARD = "overflow-hidden rounded-lg border border-[#e3e8ef] bg-white";

function profileFromSearch(value: string | null): UsageProfileBucket | null {
  return USAGE_PROFILE_ORDER.find((bucket) => bucket === value) ?? null;
}

/** Quantas roles a linha mostra antes do botão: usuário com 40 roles tomava a tela. */
const VISIBLE_ROLES = 6;

/**
 * A lista sem repetição, na ordem em que chegou. O usuário real traz a mesma role
 * uma vez por período de validade da atribuição, e repetir na tela só ocupa linha.
 */
function unique(values: string[]): string[] {
  return [...new Set(values)];
}

const HEAD = "px-2 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-2 py-2 align-top text-[#08254c]";

/** O tipo do usuário pelo nome, com a letra da SAP na frente. */
function useTypeLabel(): (userType: string) => string {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    A: `A · ${t("Dialog")}`,
    B: `B · ${t("System")}`,
    C: `C · ${t("Communication")}`,
    S: `S · ${t("Service")}`,
    L: `L · ${t("Reference")}`,
  };
  return (userType) => labels[userType] ?? (userType || "—");
}

/**
 * Usuários ativos do Ato 1, com tipo, papéis e SKU sugerido (#1478).
 *
 * Aberta pela caixa "Usuários ativos" e pelo quadro de perfil de uso. O consultor
 * usa esta lista para responder ao cliente por que alguém caiu numa licença: os
 * papéis que o usuário tem e o SKU que o Functional sugeriu. Sem nome de pessoa e
 * sem valor financeiro.
 *
 * O usuário técnico (sistema, comunicação, serviço e referência) fica num bloco
 * à parte, porque a SAP o deixa fora da medição. Execução anterior à regra ainda
 * o contou entre os ativos, e a tela diz isso em vez de esconder: o número da
 * caixa do Ato 1 só muda na próxima execução.
 */
export function ActiveUsersDetail(_props: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { data, isLoading, isError, refetch } = useActiveUsers(projectId);
  // O card de perfil de uso abre a lista já filtrada (#1478); o filtro fica na URL
  // para o "Voltar" e o link direto chegarem no mesmo recorte.
  const [search, setSearch] = useSearchParams();
  const profile = profileFromSearch(search.get("profile"));
  const chooseProfile = (bucket: UsageProfileBucket | null) => {
    const next = new URLSearchParams(search);
    if (bucket) next.set("profile", bucket);
    else next.delete("profile");
    setSearch(next, { replace: true });
  };

  if (isLoading && !data) {
    return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
  }

  if (isError || !data) {
    return (
      <div
        data-testid="active-users-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the active users.")}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  if (!data.execution_id) {
    return (
      <p
        data-testid="active-users-empty"
        className="rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3 text-[13px] text-[#596b85]"
      >
        {t("The list appears after the next Functional analysis.")}
      </p>
    );
  }

  const countedByRun = data.technical_users.filter((user) => user.counted_by_run).length;

  return (
    <div className="space-y-5">
      <p
        data-testid="active-users-summary"
        className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]"
      >
        {t(
          "Active users in this list: {{count}}. Each one with the roles it has and the license the analysis suggested.",
          { count: formatCount(data.users.length) },
        )}
      </p>

      <div className={CARD}>
        <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] px-4 py-3">
          <FilterButton active={profile === null} onClick={() => chooseProfile(null)}>
            {t("All")} {formatCount(data.users.length)}
          </FilterButton>
          {USAGE_PROFILE_ORDER.map((bucket) => {
            const count = data.users.filter(
              (user) => user.usage_profile === bucket,
            ).length;
            return (
              <FilterButton
                key={bucket}
                active={profile === bucket}
                onClick={() => chooseProfile(bucket)}
              >
                {usageProfileLabel(bucket, t)} {formatCount(count)}
              </FilterButton>
            );
          })}
        </div>
        <UsersTable
          rows={
            profile
              ? data.users.filter((user) => user.usage_profile === profile)
              : data.users
          }
          testId="active-users-list"
          rowPrefix="active-user"
        />
      </div>

      {data.technical_users.length > 0 && (
        <section data-testid="active-users-technical" className="space-y-2">
          <h3 className="text-[13px] font-semibold text-[#08254c]">
            {t("Technical users, outside the count")}
          </h3>
          <p className="max-w-3xl text-[12px] leading-relaxed text-[#596b85]">
            {t(
              "SAP leaves system, communication, service and reference users out of the license measurement.",
            )}
          </p>
          {countedByRun > 0 && (
            <p
              data-testid="active-users-counted-by-run"
              className="max-w-3xl rounded-md border border-[#f2d9a6] bg-[#fdf6e7] px-3 py-2 text-[12px] leading-relaxed text-[#8a5a00]"
            >
              {t(
                "Technical users this analysis still counted among the active ones: {{count}}. The next analysis leaves them out.",
                { count: formatCount(countedByRun) },
              )}
            </p>
          )}
          <div className={CARD}>
            <UsersTable
              rows={data.technical_users}
              testId="active-users-technical-list"
              rowPrefix="technical-user"
            />
          </div>
        </section>
      )}
    </div>
  );
}

function UsersTable({
  rows,
  testId,
  rowPrefix,
}: {
  rows: (ActiveUser | TechnicalActiveUser)[];
  testId: string;
  rowPrefix: string;
}) {
  const { t } = useTranslation();
  const typeLabel = useTypeLabel();

  return (
    <div className="overflow-x-auto">
      <table data-testid={testId} className="w-full min-w-[720px] border-collapse text-[12px]">
        <thead>
          <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
            <th className={HEAD}>{t("User")}</th>
            <th className={HEAD}>{t("Type")}</th>
            <th className={HEAD}>{t("Usage profile")}</th>
            <th className={HEAD}>{t("Roles")}</th>
            <th className={HEAD}>{t("Modules")}</th>
            <th className={HEAD}>{t("Suggested license")}</th>
            <th className={HEAD}>{t("SKU")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.username}
              data-testid={`${rowPrefix}-${row.username}`}
              className="border-b border-[#eef1f5] last:border-b-0"
            >
              <td className={`${CELL} font-mono`}>{row.username}</td>
              <td className={CELL}>{typeLabel(row.user_type)}</td>
              <td className={CELL}>
                <ProfileCell profile={row.usage_profile} />
              </td>
              <td className={CELL}>
                <RolesCell roles={row.roles} />
              </td>
              <td className={CELL}>
                {row.modules.length ? unique(row.modules).join(", ") : "—"}
              </td>
              <td className={CELL}>
                {row.license_type_name ?? "—"}
                {row.is_ambiguous && (
                  <span className="ml-2 rounded-full bg-[#fdf6e7] px-2 py-0.5 text-[10px] font-semibold text-[#8a5a00]">
                    {t("Ambiguous")}
                  </span>
                )}
              </td>
              <td className={`${CELL} font-mono`}>{row.sku_number ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** O perfil de uso do usuário, com o nome do card do Ato 1 (#1478). O perfil vem
 * pronto do servidor, com a regra do card (#1770). */
function ProfileCell({ profile }: { profile?: string | null }) {
  const { t } = useTranslation();
  const bucket = USAGE_PROFILE_ORDER.find((known) => known === profile);
  return <>{bucket ? usageProfileLabel(bucket, t) : "—"}</>;
}

/** As roles de um usuário, sem repetição, recolhidas nas primeiras {@link VISIBLE_ROLES}. */
function RolesCell({ roles }: { roles: string[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const all = unique(roles);
  if (!all.length) return <>—</>;

  const hidden = all.length - VISIBLE_ROLES;
  const shown = expanded || hidden <= 0 ? all : all.slice(0, VISIBLE_ROLES);

  return (
    <div>
      <ul className="space-y-0.5 font-mono text-[11px]">
        {shown.map((role) => (
          <li key={role}>{role}</li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="mt-1 text-[11px] font-semibold text-[#08254c] hover:underline"
        >
          {expanded ? t("Show less") : t("+{{count}} roles", { count: hidden })}
        </button>
      )}
    </div>
  );
}
